import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiZap } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { getCurrentQuickVote, castQuickVote } from '../services/api';
import { getOrCreateAnonId } from '../utils/anonId';
import { track } from '../utils/analytics';
import { notifyActionFailed } from '../utils/actionError';
import { useAnonUpgradePrompt } from '../contexts/AnonUpgradeContext';
import '../styles/QuickVote.css';

/*
 * QuickVote — home-topbar inline two-option vote widget. Occupies
 * the slot the search input used to live in.
 *
 * Lifecycle per matchup:
 *   1. fetch GetCurrentQuickVote
 *   2. show prompt + 2 option buttons
 *   3. user clicks an option → CastQuickVote
 *   4. show result row (option labels with percent + brand-orange
 *      highlight on the user's pick) for AUTO_ROTATE_MS
 *   5. fetch next quick vote, repeat
 *
 * Crucially, the widget ALWAYS renders something in the slot:
 *   - 'loading'     → subtle skeleton
 *   - 'idle'        → prompt + 2 vote buttons
 *   - 'voting'      → buttons disabled (in-flight)
 *   - 'result'      → result bars
 *   - 'empty'       → "More votes coming soon" placeholder
 * Never returns null. This avoids the empty-slot regression that
 * landed in the first cut.
 *
 * Anon support via utils/anonId.js — anon votes count toward the
 * regular per-browser vote cap.
 */

const AUTO_ROTATE_MS = 4000;

const QuickVote = () => {
  const [matchup, setMatchup] = useState(null);
  const [phase, setPhase] = useState('loading'); // loading | idle | voting | result | empty
  const [picked, setPicked] = useState(null);
  const [totals, setTotals] = useState([]);
  const [nextUrl, setNextUrl] = useState('');
  // Matchup detail URL, available BEFORE voting (from GetCurrentQuickVote's
  // landing_url). The prompt links here so users can see what the vote is
  // about instead of having to blind-vote first (#14).
  const [matchupUrl, setMatchupUrl] = useState('');
  const rotateTimerRef = useRef(null);
  const { promptUpgrade } = useAnonUpgradePrompt();

  const loadNext = useCallback(async () => {
    setPhase('loading');
    try {
      const res = await getCurrentQuickVote();
      const m = res?.data?.matchup ?? null;
      if (!m || !Array.isArray(m.options) || m.options.length !== 2) {
        setMatchup(null);
        setPhase('empty');
        return;
      }
      setMatchup(m);
      setPicked(null);
      setTotals([]);
      setNextUrl('');
      setMatchupUrl(m.landing_url || '');
      setPhase('idle');
    } catch (err) {
      console.warn('QuickVote load failed', err);
      setMatchup(null);
      setPhase('empty');
    }
  }, []);

  useEffect(() => {
    loadNext();
    return () => {
      if (rotateTimerRef.current) {
        clearTimeout(rotateTimerRef.current);
        rotateTimerRef.current = null;
      }
    };
  }, [loadNext]);

  const handleVote = useCallback(
    async (optionId) => {
      if (!matchup || phase !== 'idle') return;
      setPhase('voting');
      setPicked(optionId);
      try {
        const anonId = getOrCreateAnonId();
        const res = await castQuickVote({
          matchup_id: matchup.id,
          option_id: optionId,
          anon_id: anonId,
        });
        track('quickvote_cast', { matchup_id: matchup.id, option_id: optionId });
        const data = res?.data || {};
        setTotals(Array.isArray(data.totals) ? data.totals : []);
        setNextUrl(data.next_url || '');
        setPhase('result');
        rotateTimerRef.current = setTimeout(() => {
          loadNext();
        }, AUTO_ROTATE_MS);
      } catch (err) {
        console.warn('QuickVote cast failed', err);
        setPhase('idle');
        setPicked(null);
        // The buttons used to just re-enable, so the vote looked ignored.
        // Same mapping as a matchup-page vote: the anon cap opens the
        // sign-up prompt, anything else says it didn't count.
        const code = err?.response?.data?.code;
        const message = String(err?.response?.data?.message || '').toLowerCase();
        if (code === 'resource_exhausted' && message.includes('free vote')) {
          promptUpgrade('cap');
        } else {
          notifyActionFailed('count your vote', err);
        }
      }
    },
    [matchup, phase, loadNext, promptUpgrade],
  );

  return (
    <div
      className={`home-quickvote home-quickvote--${phase}`}
      role="region"
      aria-label="Quick Vote"
    >
      <span className="home-quickvote__label">
        <FiZap aria-hidden="true" />
        <span>QUICK VOTE</span>
      </span>

      {phase === 'loading' && (
        <span className="home-quickvote__prompt home-quickvote__prompt--skeleton" aria-hidden="true">
          &nbsp;
        </span>
      )}

      {phase === 'empty' && (
        <>
          <span className="home-quickvote__prompt home-quickvote__prompt--muted">
            No quick votes right now — check back soon.
          </span>
          <Link to="/home" className="home-quickvote__cta" aria-label="Browse matchups">
            Browse matchups →
          </Link>
        </>
      )}

      {(phase === 'idle' || phase === 'voting') && matchup && (
        <>
          {matchupUrl ? (
            <Link
              to={matchupUrl}
              className="home-quickvote__prompt home-quickvote__prompt--link"
              title={matchup.prompt}
              aria-label={`Open matchup: ${matchup.prompt}`}
            >
              {matchup.prompt}
            </Link>
          ) : (
            <span className="home-quickvote__prompt" title={matchup.prompt}>
              {matchup.prompt}
            </span>
          )}
          <div className="home-quickvote__options">
            {matchup.options.map((opt) => (
              <button
                key={opt.id}
                type="button"
                className="home-quickvote__option"
                onClick={() => handleVote(opt.id)}
                disabled={phase === 'voting'}
                aria-label={`Vote for ${opt.label}`}
              >
                {opt.icon && <span aria-hidden="true">{opt.icon}</span>}
                <span>{opt.label}</span>
              </button>
            ))}
          </div>
        </>
      )}

      {phase === 'result' && matchup && (
        <>
          {matchupUrl ? (
            <Link
              to={matchupUrl}
              className="home-quickvote__prompt home-quickvote__prompt--link"
              title={matchup.prompt}
              aria-label={`Open matchup: ${matchup.prompt}`}
            >
              {matchup.prompt}
            </Link>
          ) : (
            <span className="home-quickvote__prompt" title={matchup.prompt}>
              {matchup.prompt}
            </span>
          )}
          <div className="home-quickvote__result">
            {matchup.options.map((opt) => {
              const t = totals.find((x) => x.option_id === opt.id);
              const percent = t?.percent ?? 0;
              const isPick = opt.id === picked;
              return (
                <div
                  key={opt.id}
                  className={`home-quickvote__bar${isPick ? ' home-quickvote__bar--picked' : ''}`}
                  aria-label={`${opt.label}: ${percent}%`}
                >
                  <span className="home-quickvote__bar-fill" style={{ width: `${percent}%` }} />
                  <span className="home-quickvote__bar-text">
                    <span>{opt.label}</span>
                    <span>{percent}%</span>
                  </span>
                </div>
              );
            })}
            {nextUrl && (
              <Link to={nextUrl} className="home-quickvote__cta">
                See full discussion →
              </Link>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default QuickVote;
